// Transport for the storefront commerce blocks (`cart_checkout`,
// `my_records`, `reviews`) — the seam between the shared renderers
// and whichever server answers on this surface.
//
// The host provides it through `PageDataBlocksContext` (the frontend's
// `pageDataBlocksApiFor` in api/website.ts: the public site doors under
// `/api/website/{owner}/{project}/…` for a published / site-viewer page, the
// Run-view twins under `/api/livepreview/…` for the in-app preview and the
// Run view). No provider = the designer canvas or a surface with no server
// (a downloaded runtime): the blocks then draw sample content or say they are
// not available here. Shaped like `ReaderApi` (readerBlocks.tsx).
import { createContext, type ReactNode } from 'react'

/** A refusal from a commerce door. `code` is the server's stable code
 *  (`SIGN_IN_REQUIRED`, `CART_EMPTY`, `VALIDATION_FAILED`, …). */
export class CommerceApiError extends Error {
  code: string
  status?: number
  details?: { errors?: { field?: string; column?: string; message?: string }[] } & Record<string, unknown>
  constructor(message: string, code: string, status?: number, details?: CommerceApiError['details']) {
    super(message)
    this.name = 'CommerceApiError'
    this.code = code
    this.status = status
    this.details = details
  }
}

export interface SavedAddress {
  id: string
  label: string
  /** Keyed by the checkout FIELD key. */
  values: Record<string, string>
}

export interface CheckoutBootstrap {
  signed_in: boolean
  requires_sign_in: boolean
  me: { name: string; email: string; phone?: string } | null
  addresses: SavedAddress[]
}

export interface PlaceOrderBody {
  values: Record<string, string>
  lines: { sku: string; qty: number; start?: string; end?: string; units?: number }[]
  save_address?: boolean
  checkout_id: string
}

export interface PlaceOrderResult {
  txn_id: string | null
  reference: string | number | null
  total: number | string | null
  address_saved: boolean
  pending_approval?: boolean
}

/** The server's answer for a checkout code (`cart_checkout.promo`). */
export interface PromoCheck {
  valid: boolean
  code: string
  percent?: number
  /** Who shared it (creator handle, partner name) — '' when not mapped. */
  label?: string
  min_order?: number | null
  message?: string
}

export interface RecordLine {
  sku: string | null
  name: string | null
  qty: number | null
  unit_price: number | null
  amount: number | null
  image: string | null
}

export interface RecordSummary {
  id: string
  reference: string | number | null
  date: string | null
  status: string | null
  payment_status: string | null
  total: number | string | null
  subtotal?: number | string | null
  discount?: number | string | null
  discount_code?: string | null
  tax?: number | string | null
  shipping?: number | string | null
  paid_amount?: number | string | null
  payment_method?: string | null
  address?: string | null
  lines: RecordLine[]
}

export interface MyRecordsResult {
  records: RecordSummary[]
  total: number
  offset: number
  limit: number
  truncated?: boolean
}

/** One saved entry of a `saved_records` block, values keyed by FIELD key. */
export interface SavedRecord {
  id: string
  values: Record<string, string>
  is_default: boolean
  /** False once the block's `editableWhen` no longer holds (server-decided). */
  editable?: boolean
}

export interface SavedRecordsResult {
  records: SavedRecord[]
  /** Choices per select field, resolved by the server (e.g. the gateway's methods). */
  options: Record<string, { value: string; label: string }[]>
  max: number
  has_default: boolean
  allow_delete?: boolean
  /** The signed-in account, for pre-filling a new entry. */
  me?: { name?: string; email?: string; phone?: string } | null
}

export interface PageDataBlocksApi {
  checkoutBootstrap: (blockId: string) => Promise<CheckoutBootstrap>
  /** `embedToken` is the block's own page-bound token (`_embedToken`). */
  placeOrder: (blockId: string, body: PlaceOrderBody, embedToken?: string) => Promise<PlaceOrderResult>
  /** Check a `promo` code. Absent on a surface with no such door — the code
   *  is then still saved with the order, just not previewed. */
  checkPromo?: (blockId: string, code: string, subtotal: number) => Promise<PromoCheck>
  myRecords: (blockId: string, offset: number, record?: string) => Promise<MyRecordsResult>
  listReviews: (blockId: string, record: string, offset: number) =>
    Promise<import('./reviews').ReviewsResponse>
  addReview: (blockId: string, payload: import('./reviews').ReviewPayload) =>
    Promise<{ status: string }>
  /** `saved_records` doors. Absent on a surface without them — the block
   *  then says it is not available here. */
  savedList?: (blockId: string) => Promise<SavedRecordsResult>
  savedSave?: (blockId: string, body: { id?: string; values: Record<string, string>; make_default?: boolean }) =>
    Promise<{ ok: boolean; id: string }>
  savedDefault?: (blockId: string, id: string) => Promise<{ ok: boolean }>
  savedDelete?: (blockId: string, id: string) => Promise<{ ok: boolean }>
  /** Host override for the sign-in card (else the shared SignInPromptCard). */
  renderSignIn?: (code: string, message: string) => ReactNode
  /** Host's identify-first sign-in step (email / mobile → sign in, or create
   *  the account), rendered by `cart_checkout` when an account is needed and
   *  missing. Call `onSignedIn` once a session is in force; the checkout then
   *  re-runs its bootstrap. Absent → the shared sign-in card. */
  renderIdentify?: (opts: {
    onSignedIn: () => void; reason: string; message: string
    /** Render without a card of its own (the block frames it). */
    bare?: boolean
    labels?: { heading?: string }
  }) => ReactNode
  /** Host sign-in navigation for the reviews block's "Sign in" link. */
  signIn?: () => void
}

export const PageDataBlocksContext = createContext<PageDataBlocksApi | null>(null)

/**
 * "The visitor's records changed" — fired by a block that wrote data (a
 * `saved_records` save / delete), heard by every runtime on screen, which
 * re-reads its live data sources. That is what lets the rest of the page —
 * and the site header's menu — show the new state (an application now "in
 * review") without a reload. `object` is informative: a page often reads the
 * same table through a different object, so listeners refresh every source.
 */
export const RECORDS_CHANGED_EVENT = 'pg:records-changed'

export function notifyRecordsChanged(object?: string): void {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(new CustomEvent(RECORDS_CHANGED_EVENT, { detail: { object: object || '' } }))
  } catch { /* an old browser without CustomEvent: the next load shows it */ }
}

/** Codes that mean "this needs a (different) signed-in account". */
export const COMMERCE_SIGN_IN_CODES = ['SIGN_IN_REQUIRED', 'SESSION_EXPIRED', 'PROJECT_USER_REQUIRED']
